import { useEffect, useMemo, useState } from "react";
import { composerBus } from "@/lib/composerBus";
import { type ContentBrief, type ContentBriefDraft } from "@/lib/composerCore";
import { type Snapshot } from "./composerPageHelpers";

interface Args {
  snapshot: Snapshot;
  latestDraftByBriefId: Map<string, ContentBriefDraft | null>;
}

export function useCanvasBriefSelection({ snapshot, latestDraftByBriefId }: Args) {
  const [canvasBriefIdOverride, setCanvasBriefIdOverride] = useState<string | null>(null);
  const [forceBlankCanvas, setForceBlankCanvas] = useState(false);
  const [activeRevisionByBrief, setActiveRevisionByBrief] = useState<Record<string, string>>({});

  const activeBriefForCanvas = useMemo<ContentBrief | null>(() => {
    if (forceBlankCanvas) return null;
    if (snapshot.briefs.length === 0) return null;
    if (canvasBriefIdOverride) {
      const match = snapshot.briefs.find((b) => b.id === canvasBriefIdOverride);
      if (match) return match;
    }
    const sorted = [...snapshot.briefs].sort((a, b) => {
      const aT = new Date(a.updated_at ?? a.created_at ?? 0).getTime();
      const bT = new Date(b.updated_at ?? b.created_at ?? 0).getTime();
      return bT - aT;
    });
    return sorted[0] ?? null;
  }, [snapshot.briefs, canvasBriefIdOverride, forceBlankCanvas]);

  // Open Piece (from Archive) listener.
  useEffect(() => {
    return composerBus.subscribe("piece:open:ack", ({ pieceId }) => {
      const exists = snapshot.briefs.some((b) => b.id === pieceId);
      if (!exists) return;
      setForceBlankCanvas(false);
      setCanvasBriefIdOverride(pieceId);
      setActiveRevisionByBrief((prev) => {
        if (!(pieceId in prev)) return prev;
        const next = { ...prev };
        delete next[pieceId];
        return next;
      });
    });
  }, [snapshot.briefs]);

  const activeCanvasDraft = useMemo<ContentBriefDraft | null>(() => {
    if (!activeBriefForCanvas) return null;
    const overrideId = activeRevisionByBrief[activeBriefForCanvas.id];
    if (overrideId) {
      const match = snapshot.briefDrafts.find(
        (d) => d.id === overrideId && d.piece_id === activeBriefForCanvas.id,
      );
      if (match) return match;
    }
    return latestDraftByBriefId.get(activeBriefForCanvas.id) ?? null;
  }, [activeBriefForCanvas, activeRevisionByBrief, snapshot.briefDrafts, latestDraftByBriefId]);

  return {
    canvasBriefIdOverride,
    setCanvasBriefIdOverride,
    forceBlankCanvas,
    setForceBlankCanvas,
    activeRevisionByBrief,
    setActiveRevisionByBrief,
    activeBriefForCanvas,
    activeCanvasDraft,
  };
}
